import { AnalyticsStore } from 'bernie-plugin-mobx';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';
import { ILocalization } from 'bernie-l10n';

import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

import { UitkLayoutGrid } from 'uitk-react-layout-grid';
import { UitkSpacing, UitkSpacingHr } from 'uitk-react-spacing';
import { UitkStepInput } from 'uitk-react-step-input';
import { UitkTertiaryButton } from 'uitk-react-button';
import { UitkText } from 'uitk-react-text';

import { ChildrenAges } from './children-ages';
import { RoomPickerModel } from 'components/common/room-picker/room-picker-model';

const TertiaryButtonTracked = ClickTracker(UitkTertiaryButton);

export interface RoomProps {
  analytics?: AnalyticsStore;
  roomIndex: number;
  headerRef: React.RefObject<HTMLHeadingElement>;
  l10n: ILocalization;
  onChildAgeUpdated: (roomIndex: number, childIndex: number, target: any) => void;
  onRoomAdultsChanged: (room: any, event: any) => void;
  onRoomChildrenChanged: (room: any, event: any) => void;
  onRemoveRoom: (room: any) => void;
  room: any;
  selection: RoomPickerModel;
}

export const Room = ({
  analytics,
  roomIndex,
  headerRef,
  l10n,
  onChildAgeUpdated,
  onRoomAdultsChanged,
  onRoomChildrenChanged,
  onRemoveRoom,
  room,
  selection,
}: RoomProps) => {
  const { roomCount } = selection;
  const stepInputAnalytics = analytics?.setupTracker(':Guest', 'Adjust guest count in change search');

  const handleChildAgeUpdated = (childIndex: number, target: any) => onChildAgeUpdated(roomIndex, childIndex, target);
  const adultsChanged = (event: any) => onRoomAdultsChanged(room, event);
  const childrenChanged = (event: any) => onRoomChildrenChanged(room, event);
  const roomRemoved = () => onRemoveRoom(room);
  const roomIndexHeader = roomIndex + 1;
  const isLastRoom = selection.roomCount === roomIndexHeader;

  return (
    <UitkSpacing padding={{ blockstart: 'three' }}>
      <div key={`room-${roomCount}-${roomIndex}`} data-stid="room-count">
        <Experiment name="Vrbex_generic">
          <ExperimentControl>
            <UitkText size={200} weight="medium">
              <h3
                // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                tabIndex={0}
                className="no-outline"
                ref={isLastRoom ? headerRef : null}
                aria-label={l10n.formatText('hotels.roomPicker.room.a11yLabelRoom', roomIndexHeader, roomCount)}
              >
                <span aria-hidden="true">{l10n.formatText('hotels.roomPicker.room.title', roomIndexHeader)}</span>
              </h3>
            </UitkText>
          </ExperimentControl>
          <ExperimentVariant bucket={1} />
        </Experiment>
        <UitkStepInput
          label={l10n.formatText('hotels.roomPicker.adults.text')}
          incrementText={l10n.formatText('hotels.roomPicker.increaseAdults.text', roomIndexHeader)}
          incrementId={`room-${roomCount}-${roomIndex}-adults-increase`}
          decrementText={l10n.formatText('hotels.roomPicker.decreaseAdults.text', roomIndexHeader)}
          decrementId={`room-${roomCount}-${roomIndex}-adults-decrease`}
          max={14}
          min={1}
          value={room.adults}
          onChange={adultsChanged}
          analytics={stepInputAnalytics}
          id={`room-${roomCount}-${roomIndex}-adults`}
        />
        <UitkStepInput
          label={l10n.formatText('hotels.roomPicker.children.text')}
          incrementText={l10n.formatText('hotels.roomPicker.increaseChildren.text', roomIndexHeader)}
          incrementId={`room-${roomCount}-${roomIndex}-children-increase`}
          decrementText={l10n.formatText('hotels.roomPicker.decreaseChildren.text', roomIndexHeader)}
          decrementId={`room-${roomCount}-${roomIndex}-children-decrease`}
          description={l10n.formatText('hotels.roomPicker.children.caption')}
          max={6}
          min={0}
          value={room.children.length}
          onChange={childrenChanged}
          analytics={stepInputAnalytics}
          id={`room-${roomCount}-${roomIndex}-children`}
        />
        {room.children.length > 0 && (
          <UitkLayoutGrid columns={2} space="two">
            <div>
              {room.children.map((childAge: number, childIndex: number) => (
                <ChildrenAges
                  childAge={childAge}
                  childIndex={childIndex}
                  key={childIndex}
                  l10n={l10n}
                  onChildAgeUpdated={handleChildAgeUpdated}
                  room={room}
                  roomIndex={roomIndex}
                  selectLabel={l10n.formatText('hotels.roomPicker.childAgeInput.label', childIndex + 1)}
                  isInfant={false}
                  minChildAge={0}
                  maxChildAge={17}
                />
              ))}
            </div>
          </UitkLayoutGrid>
        )}
        {roomIndex !== 0 && (
          <UitkSpacing padding={{ blockstart: 'four' }}>
            <div>
              <UitkText align="right">
                <TertiaryButtonTracked
                  data-stid="remove-room"
                  linkName="Remove room"
                  onClick={roomRemoved}
                  rfrr=":RemoveRoom"
                  aria-label={l10n.formatText('hotels.roomPicker.removeRoomButton.a11yLabel', roomIndexHeader, roomCount)}
                >
                  {l10n.formatText('hotels.roomPicker.removeRoomButton.label')}
                </TertiaryButtonTracked>
              </UitkText>
            </div>
          </UitkSpacing>
        )}
        <UitkSpacingHr />
      </div>
    </UitkSpacing>
  );
};
